/**
 * Analytics API Service
 *
 * Provides API functions for analytics data retrieval.
 * Available for Rise and Summit subscription tiers.
 */

import { api } from './api';

// ============================================
// TYPES
// ============================================

export type DateRangeType = 'week' | 'month' | 'quarter' | 'year' | 'custom';

export interface DateRangeInfo {
  type: DateRangeType;
  label: string;
  start: string;
  end: string;
  previousStart?: string;
  previousEnd?: string;
}

export interface ChartDataPoint {
  label: string;
  value: number;
  previousValue?: number;
}

export interface MetaInfo {
  organizationId: string;
  generatedAt: string;
  placeholder?: boolean;
}

// Revenue Types
export interface RevenueSummary {
  total: number;
  previousTotal: number;
  changePercent: number;
  invoicesPaid: number;
  invoicesOutstanding: number;
  averageInvoiceValue: number;
  outstandingAmount: number;
}

export interface RevenueByService {
  service: string;
  revenue: number;
  percentage: number;
}

export interface RevenueByClient {
  clientId: string;
  clientName: string;
  revenue: number;
  percentage: number;
}

export interface RevenueChartDataPoint {
  month: string;
  revenue: number;
}

export interface RevenueStats {
  dateRange: DateRangeInfo;
  summary: RevenueSummary;
  byService: RevenueByService[];
  byClient: RevenueByClient[];
  chartData: RevenueChartDataPoint[];
  _meta: MetaInfo;
}

// Client Types
export interface ClientAnalyticsStats {
  totalClients: number;
  newThisPeriod: number;
  leadConversionRate: number;
  activeClients: number;
}

export interface ClientBySource {
  source: string;
  count: number;
}

export interface ClientByStatus {
  status: string;
  count: number;
}

export interface ClientGrowthPoint {
  date: string;
  count: number;
}

export interface ClientAnalyticsCharts {
  clientGrowth: ClientGrowthPoint[];
  bySource: ClientBySource[];
  byStatus: ClientByStatus[];
}

export interface ClientStats {
  dateRange: DateRangeInfo;
  stats: ClientAnalyticsStats;
  charts: ClientAnalyticsCharts;
  _meta: MetaInfo;
}

// Booking Types
export interface BookingAnalyticsStats {
  totalBookings: number;
  completedBookings: number;
  completionRate: number;
  noShowCount: number;
  noShowRate: number;
  cancelledCount: number;
}

export interface BookingTrendPoint {
  date: string;
  count: number;
}

export interface BookingByService {
  serviceName: string;
  count: number;
}

export interface BookingByStatus {
  status: string;
  count: number;
}

export interface BookingAnalyticsCharts {
  bookingTrend: BookingTrendPoint[];
  byService: BookingByService[];
  byStatus: BookingByStatus[];
}

export interface BookingStats {
  dateRange: DateRangeInfo;
  stats: BookingAnalyticsStats;
  charts: BookingAnalyticsCharts;
  _meta: MetaInfo;
}

// Time Tracking Types
export interface TimeAnalyticsStats {
  totalMinutes: number;
  totalHours: number;
  billableMinutes: number;
  billableHours: number;
  billablePercent: number;
  totalRevenue: number; // In cents
  revenuePerHour: number; // In cents
}

export interface TimeTrendPoint {
  date: string;
  hours: number;
}

export interface TimeByClient {
  clientId: string;
  clientName: string;
  hours: number;
}

export interface TimeByProject {
  projectId: string;
  projectName: string;
  hours: number;
}

export interface TimeAnalyticsCharts {
  timeTrend: TimeTrendPoint[];
  byClient: TimeByClient[];
  byProject: TimeByProject[];
}

export interface TimeStats {
  dateRange: DateRangeInfo;
  stats: TimeAnalyticsStats;
  charts: TimeAnalyticsCharts;
  _meta: MetaInfo;
}

// Overview Types (combined)
export interface OverviewRevenue {
  total: number;
  previousTotal: number;
  changePercent: number;
  chartData: ChartDataPoint[];
}

export interface OverviewClients {
  total: number;
  newClients: number;
  previousNewClients: number;
  changePercent: number;
  chartData: ChartDataPoint[];
}

export interface OverviewBookings {
  total: number;
  completed: number;
  cancelled: number;
  noShow: number;
  previousTotal: number;
  changePercent: number;
  chartData: ChartDataPoint[];
}

export interface OverviewTime {
  totalHours: number;
  billableHours: number;
  billableAmount: number;
  previousTotalHours: number;
  changePercent: number;
  chartData: ChartDataPoint[];
}

export interface AnalyticsOverview {
  dateRange: DateRangeInfo;
  revenue: OverviewRevenue;
  clients: OverviewClients;
  bookings: OverviewBookings;
  time: OverviewTime;
  _meta: MetaInfo;
}

// Dashboard Summary Types (lightweight for dashboard widget)
export interface DashboardSummaryData {
  period: string;
  revenue: {
    total: number;
    changePercent: number;
  };
  clients: {
    total: number;
    newThisPeriod: number;
  };
  bookings: {
    total: number;
    completionRate: number;
  };
  time: {
    totalHours: number;
    billablePercent: number;
  };
}

// Query params interface
export interface AnalyticsQueryParams {
  range?: DateRangeType;
  startDate?: string;
  endDate?: string;
}

// ============================================
// API FUNCTIONS
// ============================================

/**
 * Build query string from params
 */
function buildQueryString(params: AnalyticsQueryParams): string {
  const searchParams = new URLSearchParams();
  if (params.range) searchParams.set('range', params.range);
  if (params.startDate) searchParams.set('startDate', params.startDate);
  if (params.endDate) searchParams.set('endDate', params.endDate);
  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}

export const analyticsApi = {
  /**
   * Get overview analytics (all stats combined)
   */
  getOverview: async (params: AnalyticsQueryParams = {}): Promise<AnalyticsOverview> => {
    const query = buildQueryString(params);
    const response = await api.get(`/analytics/overview${query}`);
    return response.data.data;
  },

  /**
   * Get revenue analytics
   */
  getRevenue: async (params: AnalyticsQueryParams = {}): Promise<RevenueStats> => {
    const query = buildQueryString(params);
    const response = await api.get(`/analytics/revenue${query}`);
    return response.data.data;
  },

  /**
   * Get client analytics
   */
  getClients: async (params: AnalyticsQueryParams = {}): Promise<ClientStats> => {
    const query = buildQueryString(params);
    const response = await api.get(`/analytics/clients${query}`);
    return response.data.data;
  },

  /**
   * Get booking analytics
   */
  getBookings: async (params: AnalyticsQueryParams = {}): Promise<BookingStats> => {
    const query = buildQueryString(params);
    const response = await api.get(`/analytics/bookings${query}`);
    return response.data.data;
  },

  /**
   * Get time tracking analytics
   */
  getTime: async (params: AnalyticsQueryParams = {}): Promise<TimeStats> => {
    const query = buildQueryString(params);
    const response = await api.get(`/analytics/time${query}`);
    return response.data.data;
  },

  /**
   * Get dashboard summary (lightweight for dashboard widget)
   */
  getDashboardSummary: async (): Promise<DashboardSummaryData> => {
    const response = await api.get('/analytics/dashboard-summary');
    return response.data.data;
  },

  /**
   * Export analytics data as CSV
   * Returns a Blob that can be downloaded
   */
  exportCsv: async (
    params: AnalyticsQueryParams = {},
    type: 'overview' | 'revenue' | 'clients' | 'bookings' | 'time' = 'overview'
  ): Promise<Blob> => {
    const searchParams = new URLSearchParams();
    if (params.range) searchParams.set('range', params.range);
    if (params.startDate) searchParams.set('startDate', params.startDate);
    if (params.endDate) searchParams.set('endDate', params.endDate);
    searchParams.set('type', type);

    const response = await api.get(`/analytics/export/csv?${searchParams.toString()}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Export analytics report as PDF
   * Note: May return 501 if not yet implemented
   */
  exportPdf: async (params: AnalyticsQueryParams = {}): Promise<Blob> => {
    const query = buildQueryString(params);
    const response = await api.get(`/analytics/export/pdf${query}`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Download a blob as a file
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Format a number as currency
 */
export function formatCurrency(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount / 100); // Convert cents to dollars
}

/**
 * Format a percentage change with + or - prefix
 */
export function formatChangePercent(percent: number): string {
  const sign = percent >= 0 ? '+' : '';
  return `${sign}${percent.toFixed(1)}%`;
}

/**
 * Format hours (e.g., 1.5 -> "1h 30m")
 */
export function formatHours(hours: number): string {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}
